import { View, Text, Pressable, Platform } from "react-native";
import { Link } from "expo-router";
import { Ionicons } from "@expo/vector-icons";

/**
 * Step component for the deletion process
 */
interface StepProps {
  number: number;
  title: string;
  description: string;
}

const Step = ({ number, title, description }: StepProps) => (
  <View className="flex-row items-start gap-4 mb-6">
    <View className="w-10 h-10 rounded-full bg-red-100 items-center justify-center">
      <Text className="text-red-600 font-bold">{number}</Text>
    </View>
    <View className="flex-1">
      <Text className="text-lg font-semibold text-gray-900">{title}</Text>
      <Text className="text-gray-600 mt-1 leading-6">{description}</Text>
    </View>
  </View>
);

/**
 * Warning item component
 */
const WarningItem = ({ children }: { children: React.ReactNode }) => (
  <View className="flex-row items-start gap-3 mb-2">
    <Ionicons name="alert-circle" size={18} color="#EF4444" className="mt-1" />
    <Text className="text-gray-700 leading-6 flex-1">{children}</Text>
  </View>
);

/**
 * Account Deletion page for the Staatsen Familie App
 */
export default function DeleteAccountPage() {
  // Only render on web platform
  if (Platform.OS !== "web") {
    return null;
  }

  return (
    <View className="flex-1 min-h-screen">
      {/* Header */}
      <View className="bg-gradient-to-r from-red-600 to-rose-700 px-6 py-12">
        <View className="max-w-2xl mx-auto">
          <Link href="../(web)" asChild>
            <Pressable
              className="flex-row items-center gap-2 mb-6"
              style={{ cursor: "pointer" } as any}
            >
              <Ionicons name="arrow-back" size={20} color="white" />
              <Text className="text-white font-medium">Terug</Text>
            </Pressable>
          </Link>

          <View className="flex-row items-center gap-4">
            <View className="w-14 h-14 bg-white/20 rounded-2xl items-center justify-center">
              <Ionicons name="trash" size={28} color="white" />
            </View>
            <View>
              <Text className="text-2xl md:text-3xl font-bold text-white">
                Account Verwijderen
              </Text>
              <Text className="text-red-200 mt-1">
                Instructies voor account verwijdering
              </Text>
            </View>
          </View>
        </View>
      </View>

      {/* Content */}
      <View className="flex-1 px-6 py-12">
        <View className="max-w-2xl mx-auto w-full">
          {/* Important Notice */}
          <View className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8">
            <View className="flex-row items-start gap-3">
              <Ionicons name="warning" size={24} color="#EF4444" />
              <View className="flex-1">
                <Text className="text-lg font-semibold text-red-900">
                  Belangrijk om te weten
                </Text>
                <Text className="text-red-700 mt-2 leading-6">
                  Het verwijderen van je account is permanent en kan niet 
                  ongedaan worden gemaakt. Lees deze pagina zorgvuldig door 
                  voordat je verdergaat.
                </Text>
              </View>
            </View>
          </View>

          {/* What will be deleted */}
          <View className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-8">
            <Text className="text-xl font-bold text-gray-900 mb-4">
              Wat wordt verwijderd?
            </Text>
            <View className="ml-2">
              <WarningItem>Je profiel en accountgegevens</WarningItem>
              <WarningItem>Al je spelscores en voortgang</WarningItem>
              <WarningItem>Foto's die je hebt geüpload</WarningItem>
              <WarningItem>Chat berichten die je hebt verstuurd</WarningItem>
              <WarningItem>Alle persoonlijke voorkeuren en instellingen</WarningItem>
            </View>
          </View>

          {/* Deletion Methods */}
          <Text className="text-2xl font-bold text-gray-900 mb-6">
            Hoe verwijder ik mijn account?
          </Text>

          {/* Method 1: In-App */}
          <View className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
            <View className="flex-row items-center gap-3 mb-6">
              <View className="w-10 h-10 bg-violet-100 rounded-xl items-center justify-center">
                <Ionicons name="phone-portrait" size={20} color="#7C3AED" />
              </View>
              <Text className="text-xl font-bold text-gray-900">
                Methode 1: Via de App
              </Text>
            </View>

            <Step
              number={1}
              title="Open de App"
              description="Start de Staatsen Familie App op je telefoon en log in met je account."
            />

            <Step
              number={2}
              title="Ga naar je Profiel"
              description="Tik op het profiel-icoon in de navigatiebalk onderaan het scherm."
            />

            <Step
              number={3}
              title="Open Instellingen"
              description="Tik op het tandwiel-icoon rechtsboven of kies 'Instellingen' uit het menu."
            />

            <Step
              number={4}
              title="Account Verwijderen"
              description="Scroll naar beneden en tik op 'Account Verwijderen'. Bevestig je keuze wanneer daarom wordt gevraagd."
            />
          </View>

          {/* Method 2: Contact */}
          <View className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-8">
            <View className="flex-row items-center gap-3 mb-6">
              <View className="w-10 h-10 bg-blue-100 rounded-xl items-center justify-center">
                <Ionicons name="mail" size={20} color="#3B82F6" />
              </View>
              <Text className="text-xl font-bold text-gray-900">
                Methode 2: Via E-mail
              </Text>
            </View>

            <Text className="text-gray-600 leading-7 mb-4">
              Als je geen toegang meer hebt tot de app, kun je een 
              verwijderingsverzoek sturen naar:
            </Text>

            <View className="bg-gray-50 rounded-xl p-4 mb-4">
              <Text className="text-lg font-semibold text-blue-600 text-center">
                jesse.staats@gmail.com
              </Text>
            </View>

            <Text className="text-gray-600 leading-7">
              Vermeld in je e-mail:
            </Text>
            <View className="ml-4 mt-2">
              <Text className="text-gray-600 leading-7">• Je volledige naam</Text>
              <Text className="text-gray-600 leading-7">• Het e-mailadres van je account</Text>
              <Text className="text-gray-600 leading-7">• De reden van verwijdering (optioneel)</Text>
            </View>

            <Text className="text-gray-500 text-sm mt-4">
              We verwerken je verzoek binnen 7 werkdagen.
            </Text>
          </View>

          {/* For Parents */}
          <View className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <View className="flex-row items-start gap-3">
              <Ionicons name="people" size={24} color="#D97706" />
              <View className="flex-1">
                <Text className="text-lg font-semibold text-amber-900">
                  Account van een kind verwijderen?
                </Text>
                <Text className="text-amber-700 mt-2 leading-6">
                  Als ouder of voogd kun je het account van je kind verwijderen 
                  via dezelfde methodes hierboven. Je kunt ook contact opnemen 
                  met de familie-beheerder voor hulp.
                </Text>
              </View>
            </View>
          </View>

          {/* After deletion */}
          <View className="bg-gray-50 rounded-2xl p-6">
            <Text className="text-lg font-semibold text-gray-900 mb-3">
              Na verwijdering
            </Text>
            <Text className="text-gray-600 leading-7">
              Na het verwijderen van je account kun je altijd een nieuw account 
              aanmaken met een uitnodiging van een familielid. Je vorige 
              gegevens kunnen echter niet worden hersteld.
            </Text>
          </View>
        </View>
      </View>

      {/* Footer */}
      <View className="bg-gray-50 border-t border-gray-200 px-6 py-8">
        <View className="max-w-2xl mx-auto items-center">
          <Text className="text-sm text-gray-500 text-center">
            © {new Date().getFullYear()} Staatsen Familie App
          </Text>
        </View>
      </View>
    </View>
  );
}

